import React, { useCallback, useEffect, useMemo, useRef } from 'react';

import { useLiveQuery } from 'dexie-react-hooks';
import { Video } from 'lucide-react';
import { useLocation } from 'wouter';

import { markAllActivitiesRead } from '../../lib/activity_store';
import db, { DEXIE_IS_LOADING } from '../../lib/db';
import { assignDocument } from '../../lib/desktop_navigation_diagnostics';
import { directMessageWorkspaceSummary } from '../../lib/direct_messages_store';
import { logError } from '../../lib/logger';
import { rootDomainUrl } from '../../lib/meta';
import { appendMessageJumpParams, requestMessageJump } from '../../lib/pendingMessageJump';
import useActivityFeed from '../../lib/useActivityFeed';
import { cn } from '../../lib/utils';

import useEntitySnapshotsForText from '../../hooks/useEntitySnapshotsForText';
import { useResolvedMessage } from '../../hooks/useResolvedMessage';
import LocalTime from '../messages/LocalTime';
import {
  AUTHOR_NAME_TYPOGRAPHY_CLASS,
  MESSAGE_CONTENT_TYPOGRAPHY_CLASS,
  MESSAGE_TIMESTAMP_TYPOGRAPHY_CLASS,
  SHORT_TIME_THRESHOLD,
} from '../messages/Message';
import RichdownContent from '../messages/RichdownContent';
import { getMessageBubbleSurfaceClassName } from '../messages/messageStyleClasses';
import Flexicon from '../shared/Flexicon';
import ProfileTag from '../shared/ProfileTag';
import WidgetAnnouncementActivityContent from './WidgetAnnouncementActivityContent';

function activityResourceKey(activities) {
  if (!Array.isArray(activities)) return '';

  return activities
    .map((activity) =>
      [
        activity.id,
        activity.workspace_id,
        activity.actor_type,
        activity.actor_id,
        activity.subject_type,
        activity.subject_id,
        activity.context_type,
        activity.context_id,
        activity.navigation_target?.type,
        activity.navigation_target?.workspace_id,
        activity.navigation_target?.workspace_kind,
        activity.navigation_target?.channel_id,
        activity.navigation_target?.message_sort_order,
        activity.navigation_target?.direct_message_id,
      ].join(':'),
    )
    .join('|');
}

// Inline verbs that read as a sentence after the actor's name ("Jason mentioned you").
const ACTIVITY_ACTION_LABELS = {
  mentioned: 'mentioned you',
  replied: 'replied to you',
  reacted: 'reacted to your message',
  joined: 'joined',
  system_notice: 'notice',
  conference_started: 'started an AV call',
};

function displayAction(activity) {
  return ACTIVITY_ACTION_LABELS[activity.action] || activity.action?.replaceAll('_', ' ') || 'activity';
}

function requestMainLayoutPanel() {
  window.dispatchEvent(
    new CustomEvent('output:activate-panel', {
      detail: { panel: 'main', reason: 'activity-opened' },
    }),
  );
}

// Translate the server's navigation_target payload into a `{ path, jump }` plan:
//
//   - `path` is where to navigate (always a clean URL — no query params).
//   - `jump` (optional) is the message anchor to hand to `requestMessageJump`
//     before navigating, so MessageFeed's channel-switch drain lands centered on the
//     specific message instead of at the channel's tail.
//
// Channel targets (no message_sort_order) just navigate; only `message`
// targets carry a jump anchor.
function buildActivityNavigation(target) {
  if (!target?.type) return null;

  const channelId = target.channel_id ? String(target.channel_id) : null;
  const workspaceId = target.workspace_id ? String(target.workspace_id) : null;
  const isDirectMessage = target.workspace_kind === 'direct_message';
  const directMessageId = target.direct_message_id ? String(target.direct_message_id) : null;
  if (!channelId) return null;

  // DMs route through /messages/<dmId>; standard channels through /spaces/<wsId>/chats/<chId>.
  let path;
  if (isDirectMessage) {
    if (!directMessageId) return null;
    path = `/messages/${directMessageId}`;
  } else {
    if (!workspaceId) return null;
    path = `/spaces/${workspaceId}/chats/${channelId}`;
  }

  if (target.type === 'channel') return { path, jump: null };

  if (target.type === 'message') {
    const messageSortOrder = target.message_sort_order ? String(target.message_sort_order) : null;
    if (!messageSortOrder) return null;
    return { path, jump: { channelId, sortOrder: messageSortOrder } };
  }

  return null;
}

function formatActivityDateKey(timestamp) {
  const date = new Date((timestamp || 0) * 1000);
  return date.toLocaleDateString('en-US', { month: 'numeric', day: 'numeric', year: '2-digit' });
}

function groupActivitiesByDate(activities) {
  if (!Array.isArray(activities)) return [];

  const groups = [];
  let current = null;
  for (const activity of activities) {
    const dateKey = formatActivityDateKey(activity.timestamp);
    if (!current || current.dateKey !== dateKey) {
      current = { dateKey, items: [] };
      groups.push(current);
    }
    current.items.push(activity);
  }
  return groups;
}

function useActivityResources(activities) {
  const key = useMemo(() => activityResourceKey(activities), [activities]);

  return useLiveQuery(
    async () => {
      if (!Array.isArray(activities) || activities.length === 0) return { profiles: {}, channels: {}, workspaces: {} };

      const profileKeys = [];
      const channelKeys = [];
      const directMessageIds = new Set();
      const workspaceIds = new Set();
      const seenProfiles = new Set();
      const seenChannels = new Set();

      for (const activity of activities) {
        if (activity.workspace_id) workspaceIds.add(activity.workspace_id);
        if (activity.navigation_target?.direct_message_id) directMessageIds.add(activity.navigation_target.direct_message_id);

        if (activity.actor_type === 'profile' && activity.actor_id) {
          const profileKey = `${activity.workspace_id}:${activity.actor_id}`;
          if (!seenProfiles.has(profileKey)) {
            seenProfiles.add(profileKey);
            profileKeys.push([activity.workspace_id, activity.actor_id]);
          }
        }

        const channelId = activity.navigation_target?.channel_id || null;
        if (channelId) {
          const channelKey = `${activity.workspace_id}:${channelId}`;
          if (!seenChannels.has(channelKey)) {
            seenChannels.add(channelKey);
            channelKeys.push([activity.workspace_id, channelId]);
          }
        }
      }

      const [profiles, channels, workspaces, directMessages] = await Promise.all([
        profileKeys.length ? db.profiles.bulkGet(profileKeys) : [],
        channelKeys.length ? db.channels.bulkGet(channelKeys) : [],
        workspaceIds.size ? db.workspaces.bulkGet([...workspaceIds]) : [],
        directMessageIds.size ? db.direct_messages.bulkGet([...directMessageIds]) : [],
      ]);

      const workspaceEntries = Object.fromEntries(workspaces.filter(Boolean).map((workspace) => [workspace.id, workspace]));
      directMessages.filter(Boolean).forEach((directMessage) => {
        if (!directMessage.peer_profile) return;
        const summary = directMessageWorkspaceSummary(directMessage);
        if (summary) workspaceEntries[summary.id] = summary;
      });

      return {
        profiles: Object.fromEntries(profiles.filter(Boolean).map((profile) => [`${profile.workspace_id}:${profile.id}`, profile])),
        channels: Object.fromEntries(channels.filter(Boolean).map((channel) => [`${channel.workspace_id}:${channel.id}`, channel])),
        workspaces: workspaceEntries,
      };
    },
    [key],
    { profiles: {}, channels: {}, workspaces: {} },
  );
}

// A mini message row that stays true to the chat bubble anatomy: author name (+ tag
// and timestamp) above, avatar pinned bottom-left, the message in a real bubble.
function ActivityMessagePreview({ message, author, entitySnapshots, fallbackTimestamp }) {
  if (!message?.text) return null;
  const timestamp = message.timestamp ?? fallbackTimestamp;

  return (
    <div className="relative py-px">
      <div className="mb-1 ml-11 flex min-w-0 items-center gap-[6px]">
        <span className={cn('min-w-0 truncate text-black select-none dark:text-neutral-100', AUTHOR_NAME_TYPOGRAPHY_CLASS)}>
          {author?.full_name || 'Unknown'}
        </span>
        {author?.tag ? <ProfileTag tag={author.tag} hex={author.tag_color} className="shrink-0 select-none" /> : null}
        {timestamp ? (
          <LocalTime
            timestamp={timestamp}
            format={Date.now() / 1000 - timestamp > SHORT_TIME_THRESHOLD ? '%-m/%-d/%y · %-I:%M %p' : '%-I:%M %p'}
            className={cn('shrink-0 select-none', MESSAGE_TIMESTAMP_TYPOGRAPHY_CLASS)}
          />
        ) : null}
      </div>
      <span className="absolute bottom-px left-0 inline-flex size-9">
        <Flexicon resource={author} size="size-9" rounded="rounded-full" className="size-9" />
      </span>
      <div className="relative ml-11 flex">
        <div className={cn('richdown-bubble--base', getMessageBubbleSurfaceClassName(false), 'w-fit max-w-full')}>
          <RichdownContent
            html={message.text}
            entitySnapshots={entitySnapshots}
            richdownVariant={cn('richdown-content--channel-message-not-mine', MESSAGE_CONTENT_TYPOGRAPHY_CLASS)}
          />
        </div>
      </div>
    </div>
  );
}

function MentionActivityContent({ activity, actor }) {
  const subjectMessageId = activity.subject_type === 'message' ? activity.subject_id : null;
  const subjectChannelId = activity.navigation_target?.channel_id || null;

  // Canonical source order: durable feed cache first, API fallback for subjects outside
  // local coverage, legacy only for compatibility — a cold browser with an empty legacy
  // cache must still render the preview.
  const { message: subjectMessage } = useResolvedMessage(subjectMessageId, {
    channelId: subjectChannelId,
    allowNetwork: true,
    allowLegacy: true,
  });

  const combinedHtml = useMemo(
    () => [activity.body, subjectMessage?.text].filter(Boolean).join('\n'),
    [activity.body, subjectMessage?.text],
  );
  // Dexie-sourced, scoped to the activity's workspace — not the routed one. See the comment on
  // useEntitySnapshotsForText for why EntitySnapshotContext can't be the source here.
  const entitySnapshots = useEntitySnapshotsForText(activity.workspace_id, combinedHtml);

  return (
    <>
      {activity.body ? (
        <p className="my-5 text-sm leading-6 font-blazeit text-gray-800 dark:text-neutral-300">
          <RichdownContent html={activity.body} entitySnapshots={entitySnapshots} richdownVariant="inline" />
        </p>
      ) : null}
      <ActivityMessagePreview
        message={subjectMessage}
        author={actor}
        entitySnapshots={entitySnapshots}
        fallbackTimestamp={activity.timestamp}
      />
    </>
  );
}

function DefaultActivityContent({ activity, actor, workspace }) {
  const entitySnapshots = useEntitySnapshotsForText(activity.workspace_id, activity.body);
  const actorName = actor?.full_name || (activity.actor_type ? 'Someone' : workspace?.title || 'System');

  // Message-carrying activities (mentions, replies) render the message as a real
  // chat bubble — the bubble's author is who mentioned you, so no extra sentence.
  if (activity.body) {
    return (
      <ActivityMessagePreview
        message={{ text: activity.body, timestamp: activity.timestamp }}
        author={actor}
        entitySnapshots={entitySnapshots}
      />
    );
  }

  // Bodyless activities (reactions, joins, notices) keep a compact sentence row.
  return (
    <div className="flex min-w-0 items-center gap-2">
      {actor ? (
        <span className="inline-flex size-6 shrink-0">
          <Flexicon resource={actor} size="size-6" rounded="rounded-full" className="size-6" />
        </span>
      ) : null}
      <span className="min-w-0 truncate text-cursed">
        <span className="font-semibold text-black dark:text-neutral-100">{actorName}</span>{' '}
        <span className="text-gray-500 dark:text-neutral-400">{displayAction(activity)}</span>
      </span>
      <span className="ml-auto shrink-0 text-2xs text-gray-400 select-none dark:text-neutral-500">
        <LocalTime timestamp={activity.timestamp} type="time-ago" />
      </span>
    </div>
  );
}

// Call cards keep the compact row anatomy but lead with a video glyph so a call reads
// at a glance. Clicking the card navigates to the chat without joining the call.
function ConferenceActivityContent({ activity, actor }) {
  const actorName = actor?.full_name || 'Someone';

  return (
    <div className="flex min-w-0 items-center gap-2">
      <span className="inline-flex size-6 shrink-0">
        <Flexicon resource={actor} size="size-6" rounded="rounded-full" className="size-6" />
      </span>
      <Video className="size-4 shrink-0 text-gray-500 dark:text-neutral-400" strokeWidth={2} absoluteStrokeWidth />
      <span className="min-w-0 truncate text-cursed">
        <span className="font-semibold text-black dark:text-neutral-100">{actorName}</span>{' '}
        <span className="text-gray-500 dark:text-neutral-400">{displayAction(activity)}</span>
      </span>
      <span className="ml-auto shrink-0 text-2xs text-gray-400 select-none dark:text-neutral-500">
        <LocalTime timestamp={activity.timestamp} type="time-ago" />
      </span>
    </div>
  );
}

const USE_RICH_MENTION_ACTIVITY = false;

const ACTIVITY_CONTENT_RENDERERS = {
  ...(USE_RICH_MENTION_ACTIVITY ? { mentioned: MentionActivityContent } : {}),
  conference_started: ConferenceActivityContent,
  widget_announcement: WidgetAnnouncementActivityContent,
};

// Where: the chat it came from, and the channel when applicable. The time lives on
// the message preview itself (or the compact row for bodyless activities).
function ActivityFeedItemHeader({ workspace, channel, isDirectMessage = false }) {
  // DMs have no real channel, and activity in a chat's main channel is just "the
  // chat" — only a breakout channel disambiguates enough to be worth naming.
  const isMainChannel = Boolean(channel && workspace?.main_channel_id && String(workspace.main_channel_id) === String(channel.id));
  const showChannel = Boolean(channel) && !isDirectMessage && channel.kind !== 'direct_message' && !isMainChannel;

  return (
    <div className="-mx-3.5 flex min-w-0 items-center gap-2 border-b-[0.5px] border-black/5 px-3.5 pb-3 dark:border-white/10">
      <Flexicon resource={workspace} size="size-4" />
      <span className="min-w-0 shrink truncate text-xs font-medium text-black dark:text-neutral-100">{workspace?.title || ''}</span>
      {showChannel ? (
        <span className="min-w-0 truncate text-xs text-gray-500 dark:text-neutral-400">#{channel.name || 'channel'}</span>
      ) : null}
    </div>
  );
}

function shouldIgnoreActivityOpenEvent(event) {
  const target = event.target;
  if (!(target instanceof Element)) return false;

  const nestedInteractive = target.closest('a, button, input, select, textarea, [role="button"], entity');
  return nestedInteractive && nestedInteractive !== event.currentTarget;
}

function activityNavigationUrl(navigation) {
  const url = new URL(rootDomainUrl(navigation.path));
  appendMessageJumpParams(url, navigation.jump);
  return url.href;
}

function ActivityFeedItem({ activity, actor, channel, workspace, crossDomainLinks = false }) {
  const [, navigate] = useLocation();
  const navigation = useMemo(() => buildActivityNavigation(activity.navigation_target), [activity.navigation_target]);
  const canOpen = Boolean(navigation);
  const ContentRenderer = ACTIVITY_CONTENT_RENDERERS[activity.action] || DefaultActivityContent;

  const handleOpen = useCallback(() => {
    if (!navigation) return;
    requestMainLayoutPanel();
    if (crossDomainLinks) {
      assignDocument(activityNavigationUrl(navigation), 'workspace-switch');
      return;
    }

    // Order matters: write the jump intent into the buffer *before* navigating.
    // For cross-workspace navigations the new MessageFeed mounts asynchronously,
    // and its bootstrap reads the buffer; for same-channel jumps the live
    // MessageFeed's CustomEvent listener picks it up in the same tick.
    if (navigation.jump) requestMessageJump(navigation.jump);
    navigate(`~${navigation.path}`);
  }, [crossDomainLinks, navigate, navigation]);

  const handleOpenClick = useCallback(
    (event) => {
      if (!canOpen) return;
      if (shouldIgnoreActivityOpenEvent(event)) return;
      handleOpen();
    },
    [canOpen, handleOpen],
  );

  const handleOpenKeyDown = useCallback(
    (event) => {
      if (!canOpen) return;
      if (event.key !== 'Enter' && event.key !== ' ') return;
      if (shouldIgnoreActivityOpenEvent(event)) return;
      event.preventDefault();
      handleOpen();
    },
    [canOpen, handleOpen],
  );

  return (
    <div
      role={canOpen ? 'button' : undefined}
      tabIndex={canOpen ? 0 : undefined}
      onClick={handleOpenClick}
      onKeyDown={handleOpenKeyDown}
      className={cn(
        'flex w-full min-w-0 flex-col gap-2.5 rounded-xl border-[0.5px] border-white bg-gray-100 px-3.5 py-3 text-left dark:border-neutral-800 dark:bg-neutral-900',
        canOpen
          ? 'cursor-pointer hover:bg-gray-50 focus:outline-none focus-visible:bg-gray-200 dark:hover:bg-neutral-800 dark:focus-visible:bg-neutral-800'
          : 'cursor-default',
      )}
    >
      <ActivityFeedItemHeader
        workspace={workspace}
        channel={channel}
        isDirectMessage={activity.navigation_target?.workspace_kind === 'direct_message'}
      />
      <ContentRenderer activity={activity} actor={actor} channel={channel} workspace={workspace} />
    </div>
  );
}

function ActivityFeedDateGroup({ dateKey, items, resources, crossDomainLinks = false }) {
  return (
    <section className="flex flex-col gap-2">
      <h3 className="mt-3 mb-1 pl-2 text-xs font-semibold text-gray-600 dark:text-neutral-400">{dateKey}</h3>
      {items.map((activity) => {
        const actor = resources.profiles[`${activity.workspace_id}:${activity.actor_id}`] || null;
        const channelId = activity.navigation_target?.channel_id || null;
        const channel = channelId ? resources.channels[`${activity.workspace_id}:${channelId}`] || null : null;
        const workspace = resources.workspaces[activity.workspace_id] || null;

        return (
          <ActivityFeedItem
            key={activity.id}
            activity={activity}
            actor={actor}
            channel={channel}
            workspace={workspace}
            crossDomainLinks={crossDomainLinks}
          />
        );
      })}
    </section>
  );
}

export default function ActivityFeed({ crossDomainLinks = false }) {
  const activities = useActivityFeed({ limit: 100 });
  const resources = useActivityResources(activities);
  const isLoading = activities === DEXIE_IS_LOADING;
  const dateGroups = useMemo(() => groupActivitiesByDate(activities), [activities]);
  const attemptedReadAllRef = useRef(false);

  useEffect(() => {
    if (isLoading || attemptedReadAllRef.current) return;

    attemptedReadAllRef.current = true;
    markAllActivitiesRead().catch((error) => logError('activities', 'Failed to mark activities read on feed activation', error));
  }, [isLoading]);

  if (isLoading) {
    return <div className="flex min-h-0 flex-1 items-center justify-center text-cursed text-gray-500">Loading activity</div>;
  }

  if (!activities.length) {
    return (
      <div className="flex min-h-0 flex-1 items-center justify-center px-3 text-center text-cursed text-gray-500">No activity yet</div>
    );
  }

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="scrollbar-hidden flex min-h-0 flex-1 flex-col gap-5 overflow-y-auto px-3 pt-2 pb-24">
        {dateGroups.map((group) => (
          <ActivityFeedDateGroup
            key={group.dateKey}
            dateKey={group.dateKey}
            items={group.items}
            resources={resources}
            crossDomainLinks={crossDomainLinks}
          />
        ))}
      </div>
    </div>
  );
}
